import React, { useState } from "react";

import { Typography, Box, Alert, Stack, Button, Grid, CircularProgress } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";

import { apiClient } from "../api/client";
import { IServiceVisitDocumentDto, ServiceVisitDocumentDto } from "../api/PartnerPortalGatewayApi";
import { MainLayout } from "../components/layout/MainLayout";
import { CACard, ServiceVisitDocumentCard } from "../components/ui";
import type { RootState } from "../store";
import { useAppSelector } from "../store/hooks";

export const VisitSummaryPage: React.FC = () => {
  const selectedService = useAppSelector((state: RootState) => state.service.selectedService);

  // Date range state - default to last 30 days
  const [fromDate, setFromDate] = useState<dayjs.Dayjs>(dayjs().subtract(30, "day"));
  const [toDate, setToDate] = useState<dayjs.Dayjs>(dayjs());

  // Use React Query to load service visit documents
  const {
    data: visitDocumentsRaw = [],
    isLoading,
    error,
    refetch,
  } = useQuery<ServiceVisitDocumentDto[]>({
    queryKey: ["service-visit-documents", selectedService?.serviceId],
    queryFn: async () => {
      if (!selectedService?.serviceId) {
        throw new Error("No service selected");
      }
      return await apiClient.service.getServiceVisitDocuments(selectedService.serviceId, fromDate, toDate);
    },
    enabled: false, // Disable automatic fetching
    staleTime: 0,
    gcTime: 0,
  });

  // Transform data to add id property for DataGrid
  const visitDocuments = visitDocumentsRaw.map((doc) => ({
    ...doc,
    id: doc.documentId || Math.random(), // Use documentId as id, fallback to random number
  }));

  const handleSearch = () => {
    if (fromDate && toDate) {
      refetch();
    }
  };

  if (error) {
    return (
      <MainLayout>
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            Failed to load visit documents: {error instanceof Error ? error.message : "Unknown error"}
          </Alert>
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Box sx={{ p: 3 }}>
        <CACard sx={{ mb: 3 }}>
          <Box sx={{ padding: 2 }}>
            <Typography variant="h6" gutterBottom>
              Filter Documents
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
                <DatePicker
                  label="From Date"
                  value={fromDate}
                  onChange={(newValue) => setFromDate(newValue || dayjs())}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                    },
                  }}
                />
                <DatePicker
                  label="To Date"
                  value={toDate}
                  onChange={(newValue) => setToDate(newValue || dayjs())}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                    },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  disabled={!fromDate || !toDate || isLoading}
                  sx={{ minWidth: 120 }}
                >
                  Search
                </Button>
              </Stack>
            </LocalizationProvider>
          </Box>
        </CACard>

        <CACard>
          <Box sx={{ padding: 2 }}>
            <Typography variant="h6" gutterBottom>
              Service Visit Documents
            </Typography>

            {isLoading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {!isLoading && visitDocuments.length > 0 && (
              <Grid container spacing={2}>
                {visitDocuments.map((document) => (
                  <Grid size={{ xs: 12, sm: 12, md: 6, lg: 4 }} key={document.id}>
                    <ServiceVisitDocumentCard document={document as IServiceVisitDocumentDto} />
                  </Grid>
                ))}
              </Grid>
            )}

            {!isLoading && visitDocuments.length === 0 && (
              <Box sx={{ textAlign: "center", py: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  No visit documents found for the selected date range.
                </Typography>
              </Box>
            )}
          </Box>
        </CACard>
      </Box>
    </MainLayout>
  );
};
